import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { ChannelBrandGlyph } from "../shared/ChannelBrandIcons";

type GlyphProps = { className?: string };

/** Mini cartão de agente — sub-sistema verde/âmbar do app */
export function AgentGlyph({ className }: GlyphProps) {
  return (
    <svg viewBox="0 0 40 40" className={className} fill="none" aria-hidden>
      <rect x="4" y="6" width="32" height="28" rx="6" className="fill-[hsl(142_30%_30%)]" />
      <circle cx="16" cy="18" r="3" fill="white" fillOpacity="0.9" />
      <circle cx="24" cy="18" r="3" fill="white" fillOpacity="0.9" />
      <path
        d="M14 24c2 2 10 2 12 0"
        stroke="white"
        strokeOpacity="0.85"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <rect x="26" y="8" width="8" height="5" rx="2" className="fill-[hsl(45_90%_63%)]" />
    </svg>
  );
}

/** WhatsApp + Instagram — ícones de marca oficiais */
export function ChannelsGlyph({ className }: GlyphProps) {
  return <ChannelBrandGlyph className={className} />;
}

/** Três colunas de funil */
export function CrmGlyph({ className }: GlyphProps) {
  return (
    <svg viewBox="0 0 40 40" className={className} fill="none" aria-hidden>
      {[0, 14, 28].map((x, i) => (
        <g key={x}>
          <rect
            x={x + 2}
            y="6"
            width="10"
            height="28"
            rx="3"
            className="fill-muted stroke-border"
            strokeWidth="1"
          />
          <rect
            x={x + 4}
            y={10 + i * 3}
            width="6"
            height="4"
            rx="1"
            className="fill-primary"
            fillOpacity={0.35 + i * 0.2}
          />
          <rect x={x + 4} y={20} width="6" height="3" rx="1" className="fill-foreground/15" />
        </g>
      ))}
    </svg>
  );
}

/** Pipeline com valor */
export function DealsGlyph({ className }: GlyphProps) {
  return (
    <svg viewBox="0 0 40 40" className={className} fill="none" aria-hidden>
      <path
        d="M6 28 L14 20 L22 24 L34 10"
        stroke="hsl(var(--primary))"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {[
        [6, 28],
        [14, 20],
        [22, 24],
        [34, 10],
      ].map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r="3" className="fill-primary" fillOpacity={0.2 + i * 0.15} />
      ))}
      <rect x="24" y="28" width="12" height="6" rx="2" className="fill-primary/15" />
      <text x="26" y="32.5" fontSize="4" className="fill-primary" fontWeight="600">
        R$
      </text>
    </svg>
  );
}

/** Calendário com dia marcado */
export function ScheduleGlyph({ className }: GlyphProps) {
  return (
    <svg viewBox="0 0 40 40" className={className} fill="none" aria-hidden>
      <rect x="6" y="8" width="28" height="26" rx="4" className="fill-card stroke-border" strokeWidth="1.5" />
      <rect x="6" y="8" width="28" height="7" rx="4" className="fill-primary/20" />
      <circle cx="14" cy="22" r="2" className="fill-foreground/15" />
      <circle cx="20" cy="22" r="2" className="fill-foreground/15" />
      <circle cx="26" cy="22" r="2.5" className="fill-primary" />
      <circle cx="14" cy="28" r="2" className="fill-foreground/15" />
      <circle cx="20" cy="28" r="2" className="fill-foreground/15" />
    </svg>
  );
}

/** Relógio + bolha agendada */
export function TimedMessageGlyph({ className }: GlyphProps) {
  return (
    <svg viewBox="0 0 40 40" className={className} fill="none" aria-hidden>
      <circle cx="14" cy="16" r="10" className="stroke-primary" strokeWidth="1.5" fill="none" />
      <path d="M14 11v5l3 2" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeLinecap="round" />
      <rect x="18" y="18" width="18" height="12" rx="4" className="fill-[hsl(217_91%_60%)]" />
      <rect x="20" y="21" width="10" height="2" rx="1" fill="white" fillOpacity="0.9" />
      <rect x="20" y="25" width="7" height="2" rx="1" fill="white" fillOpacity="0.6" />
    </svg>
  );
}

/** Documento comprimido em linhas */
export function SummaryGlyph({ className }: GlyphProps) {
  return (
    <svg viewBox="0 0 40 40" className={className} fill="none" aria-hidden>
      <rect x="8" y="5" width="24" height="30" rx="3" className="fill-card stroke-border" strokeWidth="1.5" />
      {[10, 15, 20, 25, 30].map((y, i) => (
        <rect
          key={y}
          x="12"
          y={y}
          width={20 - i * 2}
          height="2"
          rx="1"
          className="fill-foreground/20"
        />
      ))}
      <path d="M26 8 L32 8 L32 14 L26 8Z" className="fill-primary/25" />
    </svg>
  );
}

/** Bolha com indicador de edição */
export function EnhanceGlyph({ className }: GlyphProps) {
  return (
    <svg viewBox="0 0 40 40" className={className} fill="none" aria-hidden>
      <rect x="4" y="10" width="22" height="14" rx="4" className="fill-muted stroke-border" strokeWidth="1" />
      <rect x="8" y="14" width="12" height="2" rx="1" className="fill-foreground/25" />
      <rect x="8" y="18" width="8" height="2" rx="1" className="fill-foreground/15" />
      <path
        d="M28 24 L36 16 L38 22 L32 28 Z"
        className="fill-primary stroke-primary"
        strokeWidth="1"
        strokeLinejoin="round"
      />
      <circle cx="36" cy="16" r="1.5" fill="white" />
    </svg>
  );
}

/** Checklist com progresso */
export function TasksGlyph({ className }: GlyphProps) {
  const rows = [
    { y: 10, done: true, width: 16 },
    { y: 20, done: true, width: 14 },
    { y: 30, done: false, width: 12 },
  ];

  return (
    <svg viewBox="0 0 40 40" className={className} fill="none" aria-hidden>
      {rows.map((row) => (
        <g key={row.y}>
          <rect
            x="8"
            y={row.y}
            width="6"
            height="6"
            rx="1.5"
            className={row.done ? "fill-primary" : "fill-card stroke-border"}
            strokeWidth={row.done ? 0 : 1}
          />
          {row.done && (
            <path
              d={`M9.5 ${row.y + 3.5} L10.8 ${row.y + 4.8} L13 ${row.y + 2.3}`}
              stroke="white"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
          <rect
            x="18"
            y={row.y + 1.5}
            width={row.width}
            height="2.5"
            rx="1"
            className="fill-foreground/18"
          />
        </g>
      ))}
    </svg>
  );
}

/** Nós de automação */
export function AutomationGlyph({ className }: GlyphProps) {
  return (
    <svg viewBox="0 0 40 40" className={className} fill="none" aria-hidden>
      <circle cx="10" cy="20" r="5" className="fill-primary/20 stroke-primary" strokeWidth="1.5" />
      <circle cx="30" cy="10" r="5" className="fill-primary/30 stroke-primary" strokeWidth="1.5" />
      <circle cx="30" cy="30" r="5" className="fill-primary/40 stroke-primary" strokeWidth="1.5" />
      <path
        d="M15 18 L24 12 M15 22 L24 28"
        stroke="hsl(var(--primary))"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeDasharray="2 2"
      />
    </svg>
  );
}

/** Tag de produto com preço */
export function CatalogGlyph({ className }: GlyphProps) {
  return (
    <svg viewBox="0 0 40 40" className={className} fill="none" aria-hidden>
      <rect x="6" y="10" width="28" height="22" rx="4" className="fill-card stroke-border" strokeWidth="1.5" />
      <rect x="10" y="14" width="10" height="10" rx="2" className="fill-muted" />
      <rect x="22" y="15" width="10" height="2" rx="1" className="fill-foreground/20" />
      <rect x="22" y="19" width="8" height="2" rx="1" className="fill-foreground/12" />
      <rect x="22" y="24" width="7" height="5" rx="2" className="fill-primary/20" />
      <text x="23.5" y="28" fontSize="3.5" className="fill-primary" fontWeight="700">
        R$
      </text>
    </svg>
  );
}

/** Barras de métricas */
export function MetricsGlyph({ className }: GlyphProps) {
  return (
    <svg viewBox="0 0 40 40" className={className} fill="none" aria-hidden>
      <rect x="6" y="30" width="28" height="2" rx="1" className="fill-border" />
      {[
        [10, 18, 6],
        [17, 12, 10],
        [24, 20, 8],
        [31, 8, 14],
      ].map(([x, y, h], i) => (
        <rect
          key={x}
          x={x}
          y={y}
          width="5"
          height={h}
          rx="1.5"
          className="fill-primary"
          fillOpacity={0.25 + i * 0.18}
        />
      ))}
    </svg>
  );
}

export function FeatureGlyphFrame({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-muted/80 ring-1 ring-border/50",
        className,
      )}
    >
      <div className="h-9 w-9">{children}</div>
    </div>
  );
}
